import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Every place this app draws a set of candidate routes (+ the shared
// pickup/deadhead leg) on a map used to hand-roll its own hidden-routes
// Set, its own color assignment, and no isolate/animation controls at all
// -- three copies that could (and did) drift: colors were assigned by an
// array *index* recomputed independently at each call site from whichever
// locally-filtered array happened to be in scope there, so the same route
// could resolve to two different colors on screen at once, and toggling
// one route off would shift everyone else's color. This hook is the one
// place that owns show/hide state, isolate-one-route, the "Christmas
// lights" sequential-reveal cycle, and color assignment -- color is keyed
// by route_type against the FULL unfiltered route list, so it can never
// shift based on what's currently hidden.
export const PICKUP_ROUTE_KEY = "__pickup__";
const ROUTE_ALT_COLORS = ["#14b8a6", "#8b5cf6", "#ec4899", "#f59e0b", "#0ea5e9"];
const PICKUP_COLOR = "var(--muted)";

export function useRouteVisibility(routes, chosenType, { includePickup = true } = {}) {
  const [hidden, setHidden] = useState(() => new Set());
  const [cycling, setCycling] = useState(false);
  const cycleRef = useRef(null);

  const routeList = routes || [];
  const allKeys = useMemo(
    () => (includePickup ? [PICKUP_ROUTE_KEY, ...routeList.map((r) => r.route_type)] : routeList.map((r) => r.route_type)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [includePickup, routeList.map((r) => r.route_type).join("|")]
  );

  const stopCycle = useCallback(() => {
    if (cycleRef.current) {
      clearInterval(cycleRef.current);
      cycleRef.current = null;
    }
    setCycling(false);
  }, []);
  useEffect(() => stopCycle, [stopCycle]);

  // Deliberately does NOT recolor the chosen/selected route to a fixed
  // color -- every call site already marks "chosen" independently (thicker/
  // opaque polylines, "In use" badges, radio-fill indicators), so forcing
  // green here just meant a route's color changed the moment it was picked,
  // discarding the color a dispatcher was using to tell routes apart on the
  // map. Confirmed real: reported as "always green" after selecting.
  const colorFor = useCallback((routeType) => {
    if (routeType === PICKUP_ROUTE_KEY) return PICKUP_COLOR;
    const idx = routeList.findIndex((r) => r.route_type === routeType);
    return ROUTE_ALT_COLORS[Math.max(idx, 0) % ROUTE_ALT_COLORS.length];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeList.map((r) => r.route_type).join("|")]);

  const isHidden = useCallback((key) => hidden.has(key), [hidden]);

  const toggle = useCallback((key) => {
    stopCycle();
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, [stopCycle]);

  // "Show only these" -- the general form of "only see the pickup route":
  // isolate(PICKUP_ROUTE_KEY) hides every candidate route,
  // isolate(someRouteType) hides the pickup leg and every other candidate.
  // Also accepts an array to keep more than one key visible at once (e.g.
  // the pickup leg AND the chosen delivery route together).
  const isolate = useCallback((keyOrKeys) => {
    stopCycle();
    const keep = Array.isArray(keyOrKeys) ? new Set(keyOrKeys) : new Set([keyOrKeys]);
    setHidden(new Set(allKeys.filter((k) => !keep.has(k))));
  }, [allKeys, stopCycle]);

  const showAll = useCallback(() => {
    stopCycle();
    setHidden(new Set());
  }, [stopCycle]);

  // Christmas-lights reveal: exactly one route (pickup included) visible
  // at a time, advancing every intervalMs, wrapping around indefinitely
  // until toggled off or another control (toggle/isolate/showAll) is used.
  const toggleCycle = useCallback((intervalMs = 3000) => {
    if (cycleRef.current) {
      stopCycle();
      return;
    }
    if (!allKeys.length) return;
    let i = 0;
    setHidden(new Set(allKeys.filter((k) => k !== allKeys[0])));
    setCycling(true);
    cycleRef.current = setInterval(() => {
      i = (i + 1) % allKeys.length;
      setHidden(new Set(allKeys.filter((k) => k !== allKeys[i])));
    }, intervalMs);
  }, [allKeys, stopCycle]);

  return { hidden, isHidden, toggle, isolate, showAll, colorFor, cycling, toggleCycle, stopCycle, PICKUP_ROUTE_KEY };
}
